import { useState, useRef, useEffect } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/lib/supabase";
import { useSEOMeta, generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEO";

const opportunities = [
  {
    icon: "ri-graduation-cap-line",
    title: "Internships",
    description: "Gain hands-on experience in public health, community development, and NGO operations.",
    color: "bg-emerald-50 text-primary",
  },
  {
    icon: "ri-heart-line",
    title: "Volunteering",
    description: "Give your time and skills to make a direct impact in communities across Cameroon.",
    color: "bg-emerald-50 text-primary",
  },
  {
    icon: "ri-team-line",
    title: "Partnerships",
    description: "Collaborate with BHODEV as an organization, institution, or corporate partner.",
    color: "bg-emerald-50 text-primary",
  },
  {
    icon: "ri-funds-line",
    title: "Fundraising",
    description: "Help us raise funds and awareness for our health and development programs.",
    color: "bg-emerald-50 text-primary",
  },
];

const areas = [
  "Health Programs", "Education & Literacy", "Economic Security",
  "Community Protection", "Research & Monitoring", "Communications & Media",
  "Finance & Accounting", "Project Management", "Fundraising",
];

function InternshipForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const country = formData.get("country") as string;
    const area = formData.get("area_of_interest") as string;
    const message = formData.get("message") as string;

    setLoading(true);

    // Insert into Supabase
    const { error } = await supabase.from("applications").insert({
      name,
      email,
      country,
      type: "Internship",
      area,
      message,
      date: new Date().toISOString(),
      status: "New",
    });

    if (error) {
      console.error("Error saving application:", error);
    }

    // Also submit to Readdy form
    try {
      const fd = new URLSearchParams();
      formData.forEach((value, key) => fd.append(key, value as string));
      await fetch("https://readdy.ai/api/form/d7o7fhaldqqj9kfl6620", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: fd.toString(),
      });
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-secondary rounded-full mb-4">
          <i className="ri-check-line text-primary text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-stone-800 mb-2 font-saria">Application Received!</h3>
        <p className="text-stone-500 text-sm max-w-sm">Thank you for your interest in interning with BHODEV. We&apos;ll review your application and get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      data-readdy-form
      id="internship-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Full Name *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              name="name"
              required
              placeholder="Your full name"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Email Address *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Country *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              name="country"
              required
              placeholder="Your country"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Area of Interest *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-briefcase-line text-stone-400 text-sm"></i>
            </div>
            <select
              name="area_of_interest"
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 appearance-none cursor-pointer"
            >
              <option value="">Select an area</option>
              {areas.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-arrow-down-s-line text-stone-400 text-sm"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Tell us what you are interested in *</label>
        <textarea
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="Describe your background, skills, and what you hope to contribute..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400 resize-none"
        />
        <p className="text-xs text-stone-400 text-right">{charCount}/500</p>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="bg-primary text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/20 flex items-center justify-center gap-2 self-end"
      >
        {loading ? (
          <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
        ) : (
          <><i className="ri-send-plane-line"></i> Submit Application</>
        )}
      </button>
    </form>
  );
}

function VolunteerForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const availability = formData.get("availability") as string;
    const area = formData.get("area_of_interest") as string;
    const message = formData.get("message") as string;

    setLoading(true);

    // Insert into Supabase
    const { error } = await supabase.from("applications").insert({
      name,
      email,
      type: "Volunteer",
      area: area || "Volunteering",
      availability,
      message,
      date: new Date().toISOString(),
      status: "New",
    });

    if (error) {
      console.error("Error saving application:", error);
    }

    // Also submit to Readdy form
    try {
      const fd = new URLSearchParams();
      formData.forEach((value, key) => fd.append(key, value as string));
      await fetch("https://readdy.ai/api/form/d7o7fialdqqj9kfl662g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: fd.toString(),
      });
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-secondary rounded-full mb-4">
          <i className="ri-check-line text-primary text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-stone-800 mb-2 font-saria">Thank You, Volunteer!</h3>
        <p className="text-stone-500 text-sm max-w-sm">Your volunteer application has been received. We&apos;ll be in touch with next steps soon.</p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      data-readdy-form
      id="volunteer-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Full Name *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              name="name"
              required
              placeholder="Your full name"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Email Address *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone Number</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="tel"
              name="phone"
              placeholder="+237 000 000 000"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Availability *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-stone-400 text-sm"></i>
            </div>
            <select
              name="availability"
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 appearance-none cursor-pointer"
            >
              <option value="">Select availability</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Weekends only">Weekends only</option>
              <option value="Remote only">Remote only</option>
            </select>
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-arrow-down-s-line text-stone-400 text-sm"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Area of Interest *</label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-briefcase-line text-stone-400 text-sm"></i>
          </div>
          <select
            name="area_of_interest"
            required
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 appearance-none cursor-pointer"
          >
            <option value="">Select an area</option>
            {areas.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-arrow-down-s-line text-stone-400 text-sm"></i>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Skills &amp; Experience *</label>
        <textarea
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="Tell us about your skills, experience, and why you want to volunteer with BHODEV..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400 resize-none"
        />
        <p className="text-xs text-stone-400 text-right">{charCount}/500</p>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="bg-primary text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/20 flex items-center justify-center gap-2 self-end"
      >
        {loading ? (
          <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
        ) : (
          <><i className="ri-heart-line"></i> Apply to Volunteer</>
        )}
      </button>
    </form>
  );
}

export default function GetInvolvedPage() {
  useSEOMeta({
    title: "Get Involved - BHODEV Cameroon | Internships, Volunteering & Partnerships",
    description:
      "Join BHODEV Cameroon as an intern, volunteer, or partner. Apply for internships in health, education, and community development. Volunteer remotely or in-person across Cameroon.",
    keywords:
      "BHODEV internship, volunteer Cameroon, NGO internship Cameroon, BHODEV volunteer, partner BHODEV, humanitarian internship, community development volunteer, Cameroon NGO jobs",
    canonicalUrl: "https://bhodevcameroon.org/get-involved",
    ogImage:
      "https://readdy.ai/api/search-image?query=diverse%20young%20African%20volunteers%20professionals%20working%20together%20community%20health%20project%20Cameroon%20teamwork%20outdoor%20lush%20green%20landscape%20warm%20natural%20light%20documentary&width=1200&height=630&seq=getinvolved-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Get Involved", url: "https://bhodevcameroon.org/get-involved" },
      ]),
      ...generateWebPageSchema(
        "Get Involved - BHODEV Cameroon",
        "Join BHODEV Cameroon as an intern, volunteer, or partner. Apply for internships in health, education, and community development.",
        "https://bhodevcameroon.org/get-involved"
      ),
    },
  });

  const [activeTab, setActiveTab] = useState<"internship" | "volunteer">("internship");
  const heroRef = useScrollAnimation();
  const oppsRef = useScrollAnimation();
  const formRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-primary-school-buildings%2FIMG-20230905-WA0029.jpg?alt=media&token=8c91d738-6029-4d4f-ab98-bd3e299e4069"
            alt="Get Involved with BHODEV"
            title="Get Involved with BHODEV - Internships, Volunteering & Partnerships"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-dark/80 via-dark/60 to-dark/30"></div>
        </div>
        <div ref={heroRef} className="relative z-10 px-4 md:px-8 lg:px-16 pt-28 pb-16 w-full">
          <div className="max-w-2xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/30 text-primary px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
              <i className="ri-hand-heart-line"></i>
              Join Our Mission
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-saria leading-tight mb-6">
              Work With Us.
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Our team is full of professionals with experience in different areas of development work. From project managers to monitoring and evaluation officers, from communication to fundraisers, finance and accountants — we always seek for improvement.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setActiveTab("internship")}
                className="bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/30"
              >
                Apply for Internship
              </button>
              <button
                onClick={() => setActiveTab("volunteer")}
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Volunteer With Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-stone-50">
        <div ref={oppsRef} className="max-w-6xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Ways to Contribute</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 font-saria">How You Can Get Involved</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {opportunities.map((opp, i) => (
              <div
                key={opp.title}
                className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-primary/30 hover:-translate-y-1 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${opp.color} mb-4`}>
                  <i className={`${opp.icon} text-xl`}></i>
                </div>
                <h3 className="text-stone-800 font-bold text-base mb-2 font-saria">{opp.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{opp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-16">
        <div ref={formRef} className="max-w-4xl mx-auto animate-fade-in-up">
          {/* Tab Switcher */}
          <div className="flex items-center justify-center mb-12">
            <div className="inline-flex bg-stone-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab("internship")}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === "internship"
                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                    : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <i className="ri-graduation-cap-line mr-2"></i>
                Internships
              </button>
              <button
                onClick={() => setActiveTab("volunteer")}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === "volunteer"
                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                    : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <i className="ri-heart-line mr-2"></i>
                Volunteering
              </button>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl border border-stone-100 overflow-hidden">
            {activeTab === "internship" ? (
              <div>
                <div className="bg-gradient-to-r from-primary to-primary-dark px-8 py-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                      <i className="ri-graduation-cap-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white font-saria mb-2">Internships.</h2>
                      <p className="text-white/80 text-sm leading-relaxed max-w-lg">
                        For all inquiries regarding internships or volunteering at BHODEV NGO, we kindly ask you to fill in the form below. If interested, feel free to contact us for volunteering, internships, research or any of our vacancies.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <InternshipForm />
                </div>
              </div>
            ) : (
              <div>
                <div className="bg-gradient-to-r from-stone-800 to-stone-900 px-8 py-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
                      <i className="ri-heart-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white font-saria mb-2">Volunteering.</h2>
                      <p className="text-white/80 text-sm leading-relaxed max-w-lg">
                        To volunteer at BHODEV, please fill in the volunteer form below and send it to info@bhodevcameroon.org together with your CV and a motivation letter. Specific volunteering positions can be posted on this page.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <VolunteerForm />
                </div>
              </div>
            )}
          </div>

          {/* Bottom CTA */}
          <div className="mt-10 bg-secondary rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl shrink-0">
                <i className="ri-mail-line text-primary"></i>
              </div>
              <div>
                <p className="text-stone-800 font-semibold text-sm">Have questions?</p>
                <p className="text-stone-500 text-xs">Reach us directly at info@bhodevcameroon.org</p>
              </div>
            </div>
            <a
              href="mailto:info@bhodevcameroon.org"
              className="bg-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Send Email
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
